import React, { useContext } from 'react';
import { Grid, Typography } from '@mui/material';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { FormProvider, useForm } from 'react-hook-form';
import { UserFormValues } from 'store/types/FormValues';
import { defaultFormProps } from 'util/Form';
import { emptyUserFormValues } from 'util/User';
import { UserContext } from 'components/UserGuard';

import styles from './ProfileForm.module.scss';
import { alternateIds } from 'store/configs/AlternateIDs';
import { useWindowSize } from 'util/Window';

interface ProfileFormProps {
  currentUserForm?: boolean;
  disabled?: boolean;
}
const defaultValues: UserFormValues = { ...emptyUserFormValues };

const ProfileForm: React.FunctionComponent<ProfileFormProps> = () => {
  const { isMobile } = useWindowSize();
  const form = useForm<UserFormValues>({ ...defaultFormProps, defaultValues });
  const userData = useContext(UserContext);

  const getAltIdType = (altIdCode: string | null) => {
    if (altIdCode == null) return null;
    return alternateIds.find((i) => i.code === altIdCode);
  };

  return (
    <FormProvider {...form}>
      <div className={styles.profileViewContainer}>
        {/* NCCER Number */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              NCCER Number
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} md={7}>
            {userData.nccerCardNumber}
          </Grid>
        </Grid>
        {!isMobile && <div className={styles.divider}></div>}
        {/* Title */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              Title
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} md={7}>
            {userData.title}
          </Grid>
        </Grid>
        {!isMobile && <div className={styles.divider}></div>}
        {/* Contact Information Section */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              Contact Information
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} {...defaultGridContainerProps} md={7}>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                First Name
              </Typography>
              <Typography className={styles.fieldValue}>{userData.firstName}</Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Last Name
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.lastName}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Email Address
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.email}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Phone Number
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.phone}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {!isMobile && <div className={styles.divider}></div>}
        {/* Personal Information */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              Personal Details
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} {...defaultGridContainerProps} md={7}>
            <Grid {...defaultGridItemProps}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Date of Birth
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.birthDate?.toDateString()}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={4}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Race
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.race}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={4}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Ethnicity
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.ethnicity}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={4}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Gender
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.gender === 'Undisclosed' ? 'Prefer not to say' : userData.gender}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Primary Language
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.nativeLanguage}
              </Typography>
            </Grid>
            {/* bottom of section */}
            {!userData.applyFerpaAgeRestriction && (
              <Grid {...defaultGridItemProps} md={4}>
                <Typography variant={'h5'} className={styles.fieldName}>
                  Current Address
                </Typography>
                <Typography paragraph={true} className={styles.fieldValue}>
                  {userData.address?.street1}
                </Typography>
                <Typography paragraph={true} className={styles.fieldValue}>
                  {userData.address?.city} {userData.address?.stateRegion} {userData.address?.postalCode}
                </Typography>
                <Typography paragraph={true} className={styles.fieldValue}>
                  {userData.address?.country}
                </Typography>
              </Grid>
            )}
            <Grid {...defaultGridItemProps} md={4}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Birth Place
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {userData.birthCity}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {!isMobile && <div className={styles.divider}></div>}
        {/* Alternate ID */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              Alternate ID
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} {...defaultGridContainerProps} md={7}>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Type
              </Typography>
              <Typography paragraph={true} className={styles.fieldValue}>
                {getAltIdType(userData.alternateIdType)?.name}
              </Typography>
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <Typography variant={'h5'} className={styles.fieldName}>
                Alternate ID Number
              </Typography>
              <Typography paragraph={true} className={styles.fieldName}>
                {userData.alternateId}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {!isMobile && <div className={styles.divider}></div>}
        {/* Photo */}
        <Grid {...defaultGridContainerProps} className={styles.row}>
          <Grid {...defaultGridItemProps} md={5}>
            <Typography variant={'h6'} className={styles.rowLabel}>
              Photo
            </Typography>
          </Grid>
          <Grid {...defaultGridItemProps} md={7} className={styles.photoDisplay}>
            {userData.logoUrl && <img className={styles.photo} src={userData.logoUrl} />}
          </Grid>
        </Grid>
      </div>
    </FormProvider>
  );
};

export default ProfileForm;
